import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API_URLS from '../../../core/common/apiUrls';
import { all_routes } from '../../router/all_routes';
import './flightAmenities.scss';
import AirplaneLoader from '../flight-loader/flightLoader';

// TypeScript Interfaces for SSR Data
interface BaggageOption {
    AirlineCode: string;
    Code: string;
    Currency: string;
    Description: number;
    Destination: string;
    FlightNumber: string;
    Origin: string;
    Price: number;
    WayType: number;
    Weight: number;
    Text?: string;
}

interface MealOption {
    AirlineCode: string;
    AirlineDescription: string;
    Code: string;
    Currency: string;
    Description: number;
    Destination: string;
    FlightNumber: string;
    Origin: string;
    Price: number;
    Quantity: number;
    WayType: number;
}

interface Seat {
    AirlineCode: string;
    AvailablityType: number;
    Code: string;
    Compartment: number;
    CraftType: string;
    Currency: string;
    Deck: number;
    Description: number;
    Destination: string;
    FlightNumber: string;
    Origin: string;
    Price: number;
    RowNo: string;
    SeatNo: string | null;
    SeatType: number;
    SeatWayType: number;
}

interface RowSeats {
    Seats: Seat[];
}

interface SegmentSeat {
    RowSeats: RowSeats[];
}

interface SeatDynamic {
    SegmentSeat: SegmentSeat[];
}

interface LegData {
    Baggage: BaggageOption[];
    MealDynamic: MealOption[];
    ResponseStatus: number;
    ResultIndex: string;
    SeatDynamic: SeatDynamic[];
    TraceId: string;
}

interface SSRResponse {
    Legs: {
        OB?: LegData;
        IB?: LegData;
    };
    TraceId: string;
    code: number;
}

interface Passenger {
    City: string;
    FirstName: string;
    IsLeadPax: boolean;
    LastName: string;
    PaxId: number;
    PaxType: number;
}

interface SavedPassengersDetails {
    FareBreakdown?: any[];
    Passengers: Passenger[];
    ReviewIds: ReviewId[];
    TempId: number;
    code: number;
    message: string;
}

interface SelectedSeat {
    paxId: number;
    seat: Seat | null;
    leg: 'OB' | 'IB'; // Track which leg the seat belongs to
}

interface FlightLeg {
    type: 'OB' | 'IB';
    data: LegData;
    label: string;
    origin: string;
    destination: string;
    flightNumber: string;
    departureTime?: string;
    arrivalTime?: string;
    duration?: string;
    airlineCode?: string;
}

interface ReviewId {
    flight_offer_id: number;
    result_index: string;
    review_id: number;
}

const FlightAmenities = () => {
    const routes = all_routes;
    const location = useLocation();
    const navigate = useNavigate();
    const [ssrData, setSsrData] = useState<SSRResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedSeats, setSelectedSeats] = useState<SelectedSeat[]>([]);
    const [selectedMeals, setSelectedMeals] = useState<Record<number, MealOption | null>>({});
    const [selectedBaggage, setSelectedBaggage] = useState<Record<number, BaggageOption | null>>({});
    const [showSeatModal, setShowSeatModal] = useState(false);
    const [currentSeat, setCurrentSeat] = useState<{ seat: Seat; leg: 'OB' | 'IB' } | null>(null);
    const [savingSSR, setSavingSSR] = useState(false);
    const [hoveredSeat, setHoveredSeat] = useState<{ seat: Seat; leg: 'OB' | 'IB' } | null>(null);
    const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
    const [activeLeg, setActiveLeg] = useState<'OB' | 'IB'>('OB'); // For round-trip flights

    // Get saved passenger details from location state
    const savedPassengersDetails = location.state?.savedPassengersDetails as SavedPassengersDetails;
    const flightDetails = location.state?.flightDetails;
    const previousRequestParams = location.state?.previousRequestParams;
    const isRoundTrip = location.state?.isRoundTrip || false;
    const isHoldMode = location.state?.isHoldMode ?? false;

    // Get unique passengers (deduplicate by name or other identifier)
    const getUniquePassengers = (): Passenger[] => {
        if (!savedPassengersDetails?.Passengers) return [];

        const uniquePassengers: Passenger[] = [];
        const seen = new Set();

        savedPassengersDetails.Passengers.forEach(passenger => {
            const key = `${passenger.FirstName}-${passenger.LastName}-${passenger.PaxType}`;
            if (!seen.has(key)) {
                seen.add(key);
                uniquePassengers.push(passenger);
            }
        });

        return uniquePassengers;
    };

    // Get the actual passenger count (not the duplicated one)
    const actualPassengerCount = getUniquePassengers().length;

    useEffect(() => {
        window.scrollTo(0, 0);

        if (!savedPassengersDetails || !previousRequestParams) {
            setError('Missing passenger details or flight parameters');
            setLoading(false);
            return;
        }

        // Get unique passengers to avoid duplication
        const uniquePassengers = getUniquePassengers();

        // Initialize selected seats array with null values for each unique passenger for each leg
        const initialSeats: SelectedSeat[] = [];
        uniquePassengers.forEach(passenger => {
            initialSeats.push({ paxId: passenger.PaxId, seat: null, leg: 'OB' });
            if (isRoundTrip) {
                initialSeats.push({ paxId: passenger.PaxId, seat: null, leg: 'IB' });
            }
        });
        setSelectedSeats(initialSeats);

        const hasAnySSR = (data: SSRResponse | null): boolean => {
            if (!data?.Legs) return false;

            return Object.values(data.Legs).some(leg =>
                (leg?.SeatDynamic?.length ?? 0) > 0 ||
                (leg?.MealDynamic?.length ?? 0) > 0 ||
                (leg?.Baggage?.length ?? 0) > 0
            );
        };

        const fetchAmenities = async () => {
            // Prepare request body based on trip type
            const body: any = {
                EndUserIp: "106.219.87.88",
                TraceId: previousRequestParams.TraceId,
            };

            if (isRoundTrip) {
                body.ResultIndexOB = previousRequestParams.ResultIndexOB;
                body.ResultIndexIB = previousRequestParams.ResultIndexIB;
            } else {
                body.ResultIndex = previousRequestParams.ResultIndex;
            }

            try {
                const response = await fetch(API_URLS.FLIGHTS.SSR, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(body),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data: SSRResponse = await response.json();
                setSsrData(data);

                // ✅ If no SSR available, skip this step
                if (!hasAnySSR(data)) {
                    navigate(routes.flightBooking, {
                        state: {
                            savedPassengersDetails,
                            flightDetails,
                            isHoldMode,
                        },
                        replace: true
                    });
                    return;
                }
            } catch (err: any) {
                setError(err.message || 'Failed to load amenities data');
                console.error('Error fetching amenities:', err);

                // Even if SSR fails, navigate to booking page
                navigate(routes.flightBooking, {
                    state: {
                        savedPassengersDetails: savedPassengersDetails,
                        flightDetails: flightDetails,
                        isHoldMode,
                    }
                });
            } finally {
                setLoading(false);
            }
        };

        fetchAmenities();
    }, [savedPassengersDetails, previousRequestParams, navigate, routes.flightBooking, flightDetails, isRoundTrip]);

    // Get available flight legs
    const getFlightLegs = (): FlightLeg[] => {
        if (!ssrData?.Legs) return [];

        const legs: FlightLeg[] = [];

        if (ssrData.Legs.OB) {
            const obData = ssrData.Legs.OB;
            const firstSeat = obData.SeatDynamic?.[0]?.SegmentSeat?.[0]?.RowSeats?.[1]?.Seats?.[0];

            legs.push({
                type: 'OB',
                data: obData,
                label: 'Outbound Flight',
                origin: firstSeat?.Origin || 'N/A',
                destination: firstSeat?.Destination || 'N/A',
                flightNumber: firstSeat?.FlightNumber || 'N/A',
                airlineCode: firstSeat?.AirlineCode || 'N/A'
            });
        }

        if (ssrData.Legs.IB) {
            const ibData = ssrData.Legs.IB;
            const firstSeat = ibData.SeatDynamic?.[0]?.SegmentSeat?.[0]?.RowSeats?.[1]?.Seats?.[0];

            legs.push({
                type: 'IB',
                data: ibData,
                label: 'Return Flight',
                origin: firstSeat?.Origin || 'N/A',
                destination: firstSeat?.Destination || 'N/A',
                flightNumber: firstSeat?.FlightNumber || 'N/A',
                airlineCode: firstSeat?.AirlineCode || 'N/A'
            });
        }

        return legs;
    };

    const FlightInfoCard = ({ leg, isActive = false }: { leg: FlightLeg, isActive?: boolean }) => {
        return (
            <div className={`flight-info-card ${isActive ? 'active' : ''}`}>
                <div className="flight-header">
                    <div className="flight-badge">
                        {leg.type === 'OB' ? 'Outbound' : 'Return'}
                    </div>
                    <div className="flight-number">
                        {leg.airlineCode} {leg.flightNumber}
                    </div>
                </div>

                <div className="flight-route">
                    <div className="route-section">
                        <div className="airport-code">{leg.origin}</div>
                    </div>

                    <div className="route-timeline">
                        <div className="route-line">
                            <div className="line"></div>
                            <div className="plane-icon">✈</div>
                        </div>
                    </div>

                    <div className="route-section">
                        <div className="airport-code">{leg.destination}</div>
                    </div>
                </div>

                <div className="flight-summary">
                    <span className="route-text">{leg.origin} to {leg.destination}</span>
                </div>
            </div>
        );
    };

    // Helper function to get city names from airport codes
    const getCityName = (airportCode: string): string => {
        const cityMap: Record<string, string> = {
            'DEL': 'Delhi',
            'BOM': 'Mumbai',
            'BLR': 'Bangalore',
            'MAA': 'Chennai',
            'HYD': 'Hyderabad',
            'CCU': 'Kolkata',
            'AMD': 'Ahmedabad',
            'PNQ': 'Pune',
            'GOI': 'Goa',
            'JAI': 'Jaipur'
            // Add more mappings as needed
        };
        return cityMap[airportCode] || airportCode;
    };

    const handleSeatSelect = (seat: Seat, leg: 'OB' | 'IB') => {
        setCurrentSeat({ seat, leg });
        setShowSeatModal(true);
    };

    const handleSeatHover = (seat: Seat, leg: 'OB' | 'IB', e: React.MouseEvent) => {
        setHoveredSeat({ seat, leg });
        setHoverPosition({ x: e.clientX, y: e.clientY });
    };

    const handleSeatLeave = () => {
        setHoveredSeat(null);
    };

    const confirmSeatSelection = (paxId: number) => {
        if (!currentSeat) return;

        setSelectedSeats(prev =>
            prev.map(item =>
                item.paxId === paxId && item.leg === currentSeat.leg
                    ? { ...item, seat: currentSeat.seat }
                    : item
            )
        );
        setShowSeatModal(false);
        setCurrentSeat(null);
    };

    const handleMealSelect = (paxId: number, meal: MealOption) => {
        setSelectedMeals(prev => ({
            ...prev,
            [paxId]: meal
        }));
    };

    const handleBaggageSelect = (paxId: number, baggage: BaggageOption) => {
        setSelectedBaggage(prev => ({
            ...prev,
            [paxId]: baggage
        }));
    };

    const handleSaveSSR = async () => {
        setSavingSSR(true);
        try {
            const uniquePassengers = getUniquePassengers();

            // Prepare seat selections - use unique passengers only
            const selections = uniquePassengers.map(passenger => {
                // Get all seats for this passenger (both OB and IB)
                const passengerSeats = selectedSeats
                    .filter(selection => selection.paxId === passenger.PaxId && selection.seat !== null)
                    .map(selection => selection.seat);

                return {
                    PaxId: passenger.PaxId,
                    Seat: passengerSeats
                };
            }).filter(selection => selection.Seat.length > 0);

            // Prepare ReviewId array - use the ReviewIds from the response
            const reviewIds = savedPassengersDetails.ReviewIds.map(review => review.review_id);

            // Construct request body
            const baseBody = {
                EndUserIp: "106.219.87.88",
                TraceId: previousRequestParams?.TraceId || "",
                ReviewId: reviewIds
            };

            // Add trip-specific parameters
            let finalBody;
            if (isRoundTrip) {
                finalBody = {
                    ...baseBody,
                    ResultIndexOB: previousRequestParams?.ResultIndexOB || "",
                    ResultIndexIB: previousRequestParams?.ResultIndexIB || "",
                    ...(selections.length > 0 && { Selections: selections })
                };
            } else {
                finalBody = {
                    ...baseBody,
                    ResultIndex: previousRequestParams?.ResultIndex || "",
                    ...(selections.length > 0 && { Selections: selections })
                };
            }

            const response = await fetch(API_URLS.FLIGHTS.SAVE_SSR, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(finalBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const responseData = await response.json();
            console.log('SSR Save successful:', responseData);

            // Navigate to booking page
            navigate(routes.flightBooking, {
                state: {
                    savedPassengersDetails: savedPassengersDetails,
                    flightDetails: flightDetails,
                    isHoldMode,
                }
            });
        } catch (err: any) {
            console.error('Error saving SSR:', err);
            setError(err.message || 'Failed to save seat selections');

            // Navigate anyway even if SSR fails
            navigate(routes.flightBooking, {
                state: {
                    savedPassengersDetails: savedPassengersDetails,
                    flightDetails: flightDetails,
                    isHoldMode,
                }
            });
        } finally {
            setSavingSSR(false);
        }
    };

    const getSeatTypeName = (seatType: number): string => {
        const seatTypes: Record<number, string> = {
            1: 'Window',
            2: 'Aisle',
            3: 'Middle',
            4: 'Extra Legroom',
            6: 'Premium',
            10: 'Standard',
            12: 'Preferred',
            16: 'Standard',
            18: 'Extra Legroom'
        };
        return seatTypes[seatType] || 'Standard';
    };

    const getAvailabilityText = (availabilityType: number): string => {
        const availabilityTypes: Record<number, string> = {
            0: 'Not Available',
            1: 'Available',
            3: 'Occupied'
        };
        return availabilityTypes[availabilityType] || 'Unknown';
    };

    const getSeatTypeClass = (seatType: number) => {
        switch (seatType) {
            case 1: return 'window';
            case 2: return 'aisle';
            case 3: return 'middle';
            case 6:
            case 18:
                return 'extra-legroom';
            case 12:
                return 'premium';
            default:
                return 'standard';
        }
    };

    // New function to determine seat layout configuration
    const determineAircraftLayout = (seats: Seat[]): { columns: number, layout: string } => {
        if (!seats || seats.length === 0) return { columns: 2, layout: '3-3' };

        const seatLetters = Array.from(
            new Set(seats.map(seat => seat.SeatNo ?? '').filter(Boolean))
        );

        // Common aircraft configurations
        if (seatLetters.includes('A') && seatLetters.includes('K')) {
            return { columns: 3, layout: '3-4-3' };
        } else if (seatLetters.includes('A') && seatLetters.includes('H') && !seatLetters.includes('I')) {
            return { columns: 3, layout: '3-3-3' };
        } else if (seatLetters.includes('A') && seatLetters.includes('F') && !seatLetters.includes('G')) {
            return { columns: 2, layout: '3-3' };
        } else if (seatLetters.includes('A') && seatLetters.includes('D') && !seatLetters.includes('E')) {
            return { columns: 2, layout: '2-2' };
        }

        return { columns: 2, layout: '3-3' };
    };

    // Function to group seats by row and column based on aircraft layout
    const organizeSeatsByRow = (seats: Seat[], layout: string) => {
        const rows: Record<string, Seat[]> = {};

        seats.forEach(seat => {
            if (!rows[seat.RowNo]) {
                rows[seat.RowNo] = [];
            }
            rows[seat.RowNo].push(seat);
        });

        Object.keys(rows).forEach(rowNumber => {
            rows[rowNumber].sort((a, b) => {
                const seatOrder = {
                    'A': 1, 'B': 2, 'C': 3,
                    'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8, 'I': 9, 'J': 10, 'K': 11
                };

                const aOrder = seatOrder[a.SeatNo as keyof typeof seatOrder] || 99;
                const bOrder = seatOrder[b.SeatNo as keyof typeof seatOrder] || 99;

                return aOrder - bOrder;
            });
        });

        return rows;
    };

    // Function to get seat groups based on aircraft layout
    const getSeatGroups = (seats: Seat[], layout: string) => {
        if (layout === '3-4-3') {
            return [
                seats.filter(seat => ['A', 'B', 'C'].includes(seat.SeatNo || '')),
                seats.filter(seat => ['D', 'E', 'F', 'G'].includes(seat.SeatNo || '')),
                seats.filter(seat => ['H', 'J', 'K'].includes(seat.SeatNo || ''))
            ];
        } else if (layout === '3-3-3') {
            return [
                seats.filter(seat => ['A', 'B', 'C'].includes(seat.SeatNo || '')),
                seats.filter(seat => ['D', 'E', 'F'].includes(seat.SeatNo || '')),
                seats.filter(seat => ['G', 'H', 'J'].includes(seat.SeatNo || ''))
            ];
        } else if (layout === '2-3-2') {
            return [
                seats.filter(seat => ['A', 'B'].includes(seat.SeatNo || '')),
                seats.filter(seat => ['C', 'D', 'E'].includes(seat.SeatNo || '')),
                seats.filter(seat => ['F', 'G'].includes(seat.SeatNo || ''))
            ];
        } else if (layout === '3-3') {
            return [
                seats.filter(seat => ['A', 'B', 'C'].includes(seat.SeatNo || '')),
                seats.filter(seat => ['D', 'E', 'F'].includes(seat.SeatNo || ''))
            ];
        } else if (layout === '2-2') {
            return [
                seats.filter(seat => ['A', 'B'].includes(seat.SeatNo || '')),
                seats.filter(seat => ['C', 'D'].includes(seat.SeatNo || ''))
            ];
        }

        return [
            seats.filter(seat => ['A', 'B', 'C'].includes(seat.SeatNo || '')),
            seats.filter(seat => ['D', 'E', 'F'].includes(seat.SeatNo || ''))
        ];
    };

    const hasSeatMap = (legData?: LegData) => {
        return !!legData?.SeatDynamic?.length;
    };

    const renderSeatMap = (legData: LegData, leg: 'OB' | 'IB') => {
        if (!hasSeatMap(legData)) {
            return (
                <div className="alert alert-info">
                    <i className="isax isax-info-circle me-2"></i>
                    Seat selection is not available for this flight.
                </div>
            );
        }

        if (!legData || !legData.SeatDynamic || !legData.SeatDynamic[0] || !legData.SeatDynamic[0].SegmentSeat) {
            return <div className="alert alert-info">No seat map available for this flight</div>;
        }

        const segmentSeat = legData.SeatDynamic[0].SegmentSeat[0];

        // Extract all seats from the response
        const allSeats: Seat[] = [];
        segmentSeat.RowSeats.forEach(rowSeats => {
            rowSeats.Seats.forEach(seat => {
                allSeats.push(seat);
            });
        });

        // Determine aircraft layout
        const { columns, layout } = determineAircraftLayout(allSeats);
        const rows = organizeSeatsByRow(allSeats, layout);

        const isAvailable = (seat: Seat) => seat.AvailablityType === 1;
        const isSelected = (seat: Seat) => selectedSeats.some(
            s => s.seat?.Code === seat.Code && s.leg === leg
        );

        return (
            <div className="seat-map-container">
                <div className="aircraft-visualization">
                    <div className="aircraft-nose">
                        <div className="nose-label">Front of Aircraft</div>
                    </div>

                    <div className={`cabin-layout ${columns === 3 ? 'three-columns' : 'two-columns'}`}>
                        <div className="left-aisle"></div>

                        <div className="seating-area">
                            {Object.entries(rows).map(([rowNumber, seats]) => {
                                const seatGroups = getSeatGroups(seats, layout);

                                return (
                                    <div key={rowNumber} className="seat-row">
                                        <div className="row-number">{rowNumber}</div>

                                        <div className="seats-group">
                                            {seatGroups.map((group, groupIndex) => (
                                                <React.Fragment key={groupIndex}>
                                                    <div className={`seat-group group-${groupIndex}`}>
                                                        {group.map(seat => {
                                                            const isAvailable = seat.AvailablityType === 1;
                                                            const isSelected = selectedSeats.some(
                                                                s => s.seat?.Code === seat.Code && s.leg === leg
                                                            );
                                                            const paid = isPaidSeat(seat);
                                                            return (
                                                                <div
                                                                    key={seat.Code}
                                                                    className={`seat ${getSeatTypeClass(seat.SeatType)} ${!isAvailable ? 'unavailable' : 'available'} ${isSelected ? 'selected' : ''} ${paid ? 'seat-paid' : 'seat-free'}`}
                                                                    onClick={() => isAvailable && handleSeatSelect(seat, leg)}
                                                                    onMouseEnter={(e) => isAvailable && handleSeatHover(seat, leg, e)}
                                                                    onMouseLeave={handleSeatLeave}
                                                                >
                                                                    <span className="seat-letter">{seat.SeatNo}</span>

                                                                    {/* 💰 Paid / Free Badge */}
                                                                    {isAvailable && (
                                                                        <span className={`seat-price-badge ${paid ? 'paid' : 'free'}`}>
                                                                            {paid ? `₹${seat.Price}` : 'FREE'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )
                                                        })}
                                                    </div>

                                                    {groupIndex < seatGroups.length - 1 && (
                                                        <div className="aisle-gap"></div>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="right-aisle"></div>
                    </div>

                    <div className="aircraft-tail">
                        <div className="tail-label">Rear of Aircraft</div>
                    </div>
                </div>

                {/* Hover tooltip for seat details */}
                {hoveredSeat && hoveredSeat.leg === leg && (
                    <div
                        className="seat-tooltip"
                        style={{ left: hoverPosition.x + 10, top: hoverPosition.y + 10 }}
                    >
                        <div className="tooltip-header">
                            <span className="seat-code">{hoveredSeat.seat.Code}</span>
                            <span className="seat-type">{getSeatTypeName(hoveredSeat.seat.SeatType)}</span>
                        </div>
                        <div className="tooltip-details">
                            <div className="tooltip-row">
                                <span className="label">Price:</span>
                                <span className={`value ${hoveredSeat.seat.Price > 0 ? 'paid' : 'free'}`}>
                                    {hoveredSeat.seat.Price > 0 ? `₹${hoveredSeat.seat.Price}` : 'Free'}
                                </span>
                            </div>
                            <div className="tooltip-row">
                                <span className="label">Status:</span>
                                <span className="value">{getAvailabilityText(hoveredSeat.seat.AvailablityType)}</span>
                            </div>
                            <div className="tooltip-row">
                                <span className="label">Row:</span>
                                <span className="value">{hoveredSeat.seat.RowNo}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="seat-map-legend">
                    <h6>Seat Pricing</h6>
                    <div className="legend-items">
                        <div className="legend-item">
                            <div className="seat seat-free legend-seat"></div>
                            <span>Free Seat</span>
                        </div>
                        <div className="legend-item">
                            <div className="seat seat-paid legend-seat"></div>
                            <span>Paid Seat</span>
                        </div>
                    </div>

                    <h6 className="mt-3">Seat Status</h6>
                    <div className="legend-items">
                        <div className="legend-item">
                            <div className="seat legend-seat"></div>
                            <span>Available</span>
                        </div>
                        <div className="legend-item">
                            <div className="seat seat-disabled legend-seat"></div>
                            <span>Occupied</span>
                        </div>
                        <div className="legend-item">
                            <div className="seat seat-selected legend-seat"></div>
                            <span>Selected</span>
                        </div>
                    </div>

                    <h6 className="mt-3">Seat Types</h6>
                    <div className="legend-items">
                        <div className="legend-item">
                            <div className="seat seat-window legend-seat"></div>
                            <span>Window</span>
                        </div>
                        <div className="legend-item">
                            <div className="seat seat-aisle legend-seat"></div>
                            <span>Aisle</span>
                        </div>
                        <div className="legend-item">
                            <div className="seat seat-middle legend-seat"></div>
                            <span>Middle</span>
                        </div>
                        <div className="legend-item">
                            <div className="seat seat-extra-legroom legend-seat"></div>
                            <span>Extra Legroom</span>
                        </div>
                        <div className="legend-item">
                            <div className="seat seat-preferred legend-seat"></div>
                            <span>Preferred</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const isPaidSeat = (seat: Seat): boolean => seat.Price > 0;

    const getSeatPriceLabel = (seat: Seat): string => {
        return seat.Price > 0 ? `₹${seat.Price}` : 'FREE';
    };

    const renderMealOptions = (legData: LegData, leg: 'OB' | 'IB') => {
        if (!legData?.MealDynamic?.length) {
            return (
                <div className="alert alert-info">
                    No meal options available for this flight
                </div>
            );
        }

        const passengers = getUniquePassengers();

        return (
            <div className="amenity-section">
                <div className="section-header">
                    <i className="isax isax-food-circle"></i>
                    <h4>Meal Selection – {leg === 'OB' ? 'Outbound' : 'Return'}</h4>
                </div>

                {passengers.map(passenger => (
                    <div key={passenger.PaxId} className="dropdown-row">
                        <label className="form-label">
                            {passenger.FirstName} {passenger.LastName}
                        </label>

                        <select
                            className="form-select"
                            value={selectedMeals[passenger.PaxId]?.Code || ''}
                            onChange={(e) => {
                                const meal =
                                    legData.MealDynamic.find(m => m.Code === e.target.value) || null;
                                if (meal) handleMealSelect(passenger.PaxId, meal);
                            }}
                        >
                            <option value="">No Meal</option>

                            {legData.MealDynamic
                                .filter(m => m.Code !== 'NoMeal')
                                .map(meal => (
                                    <option key={meal.Code} value={meal.Code}>
                                        {meal.AirlineDescription} – ₹{meal.Price}
                                    </option>
                                ))}
                        </select>
                    </div>
                ))}
            </div>
        );
    };

    const renderBaggageOptions = (legData: LegData, leg: 'OB' | 'IB') => {
        if (!legData?.Baggage?.length) {
            return (
                <div className="alert alert-info">
                    No baggage options available for this flight
                </div>
            );
        }

        const passengers = getUniquePassengers();

        return (
            <div className="amenity-section">
                <div className="section-header">
                    <i className="isax isax-bag"></i>
                    <h4>Baggage Selection – {leg === 'OB' ? 'Outbound' : 'Return'}</h4>
                </div>

                {passengers.map(passenger => (
                    <div key={passenger.PaxId} className="dropdown-row">
                        <label className="form-label">
                            {passenger.FirstName} {passenger.LastName}
                        </label>

                        <select
                            className="form-select"
                            value={selectedBaggage[passenger.PaxId]?.Code || ''}
                            onChange={(e) => {
                                const baggage =
                                    legData.Baggage.find(b => b.Code === e.target.value) || null;
                                if (baggage) handleBaggageSelect(passenger.PaxId, baggage);
                            }}
                        >
                            <option value="">No Extra Baggage</option>

                            {legData.Baggage
                                .filter(b => b.Code !== 'NoBaggage')
                                .map(baggage => (
                                    <option key={baggage.Code} value={baggage.Code}>
                                        {baggage.Weight > 0
                                            ? `${baggage.Weight}kg`
                                            : 'No Baggage'} – ₹{baggage.Price}
                                    </option>
                                ))}
                        </select>
                    </div>
                ))}
            </div>
        );
    };

    // Update the validation to check for unique passengers
    const allSeatsSelected = () => {
        const uniquePassengers = getUniquePassengers();
        const flightLegs = getFlightLegs();

        // Each unique passenger should have a seat for each flight leg
        return uniquePassengers.every(passenger =>
            flightLegs.every(leg =>
                selectedSeats.some(s =>
                    s.paxId === passenger.PaxId && s.leg === leg.type && s.seat !== null
                )
            )
        );
    };

    const renderPassengerSeatSummary = () => {
        const uniquePassengers = getUniquePassengers();
        if (!uniquePassengers.length) return null;

        const flightLegs = getFlightLegs();

        return (
            <div className="booking-summary-card">
                <div className="summary-header">
                    <h4>Booking Summary</h4>
                    {isRoundTrip && (
                        <div className="trip-badge">
                            <span className="badge bg-info">Round Trip</span>
                        </div>
                    )}
                </div>

                <div className="passenger-amenities">
                    {uniquePassengers.map(passenger => {
                        const passengerSeats = selectedSeats.filter(s => s.paxId === passenger.PaxId);
                        const passengerBaggage = selectedBaggage[passenger.PaxId];
                        const passengerMeal = selectedMeals[passenger.PaxId];

                        return (
                            <div key={passenger.PaxId} className="passenger-amenity-card">
                                <div className="passenger-header">
                                    <h5>
                                        {passenger.FirstName} {passenger.LastName}
                                        {passenger.IsLeadPax && <span className="badge">Lead Passenger</span>}
                                    </h5>
                                </div>

                                <div className="amenity-details">
                                    {/* Seat selections for each leg */}
                                    {flightLegs.map(leg => {
                                        const seatForLeg = passengerSeats.find(s => s.leg === leg.type);
                                        return (
                                            <div key={leg.type} className="amenity-row">
                                                <span className="amenity-label">
                                                    Seat ({leg.type === 'OB' ? 'Outbound' : 'Return'}):
                                                </span>
                                                {seatForLeg?.seat ? (
                                                    <div className="selected-amenity">
                                                        <span className="seat-badge">{seatForLeg.seat.Code}</span>
                                                        <span className="amenity-price">₹{seatForLeg.seat.Price}</span>
                                                    </div>
                                                ) : (
                                                    <span className="not-selected">Not selected</span>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {passengerMeal && (
                                        <div className="amenity-row">
                                            <span className="amenity-label">Meal:</span>
                                            <div className="selected-amenity">
                                                <span>{passengerMeal.AirlineDescription}</span>
                                                <span className="amenity-price">₹{passengerMeal.Price}</span>
                                            </div>
                                        </div>
                                    )}

                                    {passengerBaggage && (
                                        <div className="amenity-row">
                                            <span className="amenity-label">Baggage:</span>
                                            <div className="selected-amenity">
                                                <span>
                                                    {passengerBaggage.Weight > 0
                                                        ? `${passengerBaggage.Weight}kg`
                                                        : 'No Baggage'
                                                    }
                                                </span>
                                                <span className="amenity-price">₹{passengerBaggage.Price}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="amenity-totals">
                    <div className="total-row">
                        <span>Seats Total</span>
                        <span>
                            ₹{selectedSeats.reduce((total, selection) =>
                                total + (selection.seat?.Price || 0), 0
                            )}
                        </span>
                    </div>
                    <div className="total-row">
                        <span>Meals Total</span>
                        <span>
                            ₹{Object.values(selectedMeals).reduce((total, meal) =>
                                total + (meal?.Price || 0), 0
                            )}
                        </span>
                    </div>
                    <div className="total-row">
                        <span>Baggage Total</span>
                        <span>
                            ₹{Object.values(selectedBaggage).reduce((total, baggage) =>
                                total + (baggage?.Price || 0), 0
                            )}
                        </span>
                    </div>
                    <div className="total-divider"></div>
                    <div className="total-row final-total">
                        <span>Additional Charges Total</span>
                        <span className="total-amount">
                            ₹{selectedSeats.reduce((total, selection) =>
                                total + (selection.seat?.Price || 0), 0
                            ) + Object.values(selectedMeals).reduce((total, meal) =>
                                total + (meal?.Price || 0), 0
                            ) + Object.values(selectedBaggage).reduce((total, baggage) =>
                                total + (baggage?.Price || 0), 0
                            )}
                        </span>
                    </div>
                </div>

                <div className="proceed-btn-wrapper">
                    <button
                        className="btn btn-primary btn-lg w-70 proceed-btn"
                        onClick={handleSaveSSR}
                        disabled={savingSSR}
                    >
                        {savingSSR ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Processing...
                            </>
                        ) : hasSelectedAnySSR() ? (
                            'Save Amenities & Proceed to Booking'
                        ) : (
                            'Skip Amenities & Proceed to Booking'
                        )}
                    </button>
                </div>
            </div>
        );
    };

    const renderSeatModal = () => {
        if (!currentSeat) return null;

        return (
            <div className={`modal fade ${showSeatModal ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Select Seat for {currentSeat.leg === 'OB' ? 'Outbound' : 'Return'} Flight
                            </h5>
                            <button type="button" className="btn-close" onClick={() => setShowSeatModal(false)}></button>
                        </div>
                        <div className="modal-body">
                            <div className="seat-modal-details">
                                <div className="seat-visual">
                                    <div className={`seat-large ${getSeatTypeClass(currentSeat.seat.SeatType)}`}>
                                        <span className="seat-letter">{currentSeat.seat.SeatNo}</span>
                                    </div>
                                </div>
                                <div className="seat-info">
                                    <h6>{currentSeat.seat.Code} - {getSeatTypeName(currentSeat.seat.SeatType)} Seat</h6>
                                    <div className="seat-properties">
                                        <div className="property">
                                            <span className="label">Price:</span>
                                            <span className="value">₹{currentSeat.seat.Price}</span>
                                        </div>
                                        <div className="property">
                                            <span className="label">Row:</span>
                                            <span className="value">{currentSeat.seat.RowNo}</span>
                                        </div>
                                        <div className="property">
                                            <span className="label">Type:</span>
                                            <span className="value">{getSeatTypeName(currentSeat.seat.SeatType)}</span>
                                        </div>
                                        <div className="property">
                                            <span className="label">Flight:</span>
                                            <span className="value">{currentSeat.leg === 'OB' ? 'Outbound' : 'Return'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="passenger-selection mt-4">
                                <label className="form-label">Select Passenger:</label>
                                <select className="form-select" id="passengerSelect">
                                    {getUniquePassengers().map(passenger => (
                                        <option key={passenger.PaxId} value={passenger.PaxId}>
                                            {passenger.FirstName} {passenger.LastName}
                                            {passenger.IsLeadPax && ' (Lead)'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={() => setShowSeatModal(false)}>Cancel</button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    const selectElement = document.getElementById('passengerSelect') as HTMLSelectElement;
                                    const paxId = parseInt(selectElement.value);
                                    confirmSeatSelection(paxId);
                                }}
                            >
                                Confirm Selection
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderFlightLegTabs = () => {
        const flightLegs = getFlightLegs();

        if (flightLegs.length <= 1) return null;

        return (
            <div className="flight-leg-tabs">
                <div className="flight-info-header">
                    <h3>Select Amenities for Your Flights</h3>
                    <p>Choose seats, meals, and baggage for each leg of your journey</p>
                </div>

                <nav>
                    <div className="nav nav-tabs" id="nav-tab" role="tablist">
                        {flightLegs.map((leg) => (
                            <button
                                key={leg.type}
                                className={`nav-link ${activeLeg === leg.type ? 'active' : ''}`}
                                onClick={() => setActiveLeg(leg.type)}
                            >
                                <div className="tab-flight-info">
                                    <div className="flight-route">
                                        <span className="route">{leg.origin} → {leg.destination}</span>
                                    </div>
                                    <small>{leg.label}</small>
                                </div>
                            </button>
                        ))}
                    </div>
                </nav>
            </div>
        );
    };

    const renderContentForLeg = (leg: 'OB' | 'IB') => {
        const flightLegs = getFlightLegs();
        const legData = flightLegs.find(l => l.type === leg)?.data;
        const currentLeg = flightLegs.find(l => l.type === leg);

        if (!legData || !currentLeg) return null;

        return (
            <div className={`tab-pane fade ${activeLeg === leg ? 'show active' : ''}`} id={`nav-${leg}`} role="tabpanel">
                {/* Flight info header */}
                <div className="content-section">
                    <div className="section-header with-icon">
                        <i className="isax isax-airplane"></i>
                        <div className="flight-title">
                            <h2>{leg === 'OB' ? 'Outbound Flight' : 'Return Flight'}</h2>
                            <div className="flight-subtitle">
                                {`${currentLeg.airlineCode ? `${currentLeg.airlineCode} ` : ''} ${currentLeg.flightNumber ? `${currentLeg.flightNumber} ` : ''} ${(currentLeg.origin && currentLeg.destination) ? ` • ${currentLeg.origin} to ${currentLeg.destination}` : ''}`}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="content-section">
                    <div className="section-header with-icon">
                        <i className="isax isax-chair"></i>
                        <h3>Seat Selection {(currentLeg.origin && currentLeg.destination && currentLeg.origin !== 'N/A' && currentLeg.destination !== 'N/A') ? `- ${currentLeg.origin} to ${currentLeg.destination}` : ''}</h3>
                    </div>
                    {renderSeatMap(legData, leg)}
                </div>

                <div className="content-section">
                    {renderMealOptions(legData, leg)}
                </div>

                <div className="content-section">
                    {renderBaggageOptions(legData, leg)}
                </div>
            </div>
        );
    };

    const hasSelectedAnySSR = () => {
        const hasSeat = selectedSeats.some(s => s.seat !== null);
        const hasMeal = Object.values(selectedMeals).some(Boolean);
        const hasBaggage = Object.values(selectedBaggage).some(Boolean);
        return hasSeat || hasMeal || hasBaggage;
    };

    if (loading) {
        return (
            <div>
                <div className={`breadcrumb-bar flightList-banner-bg text-center`}>
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12 col-12">
                                <h1 className="breadcrumb-title mb-2">Customize Your Flight Experience</h1>
                                <p className="breadcrumb-subtitle mb-2">Select seats, meals, and baggage options for your flight</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flight-loading">
                    <AirplaneLoader text="Loading your flight amenities..." size="large" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <div className={`breadcrumb-bar flightList-banner-bg text-center`}>
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12 col-12">
                                <h1 className="breadcrumb-title mb-2">Customize Your Flight Experience</h1>
                                <p className="breadcrumb-subtitle mb-2">Select seats, meals, and baggage options for your flight</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flight-error">
                    <div className="error-content">
                        <i className="isax isax-close-circle"></i>
                        <h4>Something went wrong</h4>
                        <p>{error}</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate(routes.flightBooking, {
                                state: {
                                    savedPassengersDetails: savedPassengersDetails,
                                    flightDetails: flightDetails,
                                    isHoldMode,
                                }
                            })}
                        >
                            Continue to Booking
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const flightLegs = getFlightLegs();

    return (
        <div className="flight-amenities-page">
            <div className={`breadcrumb-bar flightList-banner-bg text-center`}>
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 col-12">
                            <h1 className="breadcrumb-title mb-2">Customize Your Flight Experience</h1>
                            <p className="breadcrumb-subtitle mb-2">Select seats, meals, and baggage options for your flight</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-content">
                <div className="container">
                    <div className="content-grid">
                        <div className="main-content">
                            {renderFlightLegTabs()}

                            <div className="tab-content" id="nav-tabContent">
                                {flightLegs.map(leg => renderContentForLeg(leg.type))}
                            </div>
                        </div>

                        <div className="sidebar-content">
                            {renderPassengerSeatSummary()}
                        </div>
                    </div>
                </div>
            </div>

            {renderSeatModal()}
        </div>
    );
};

export default FlightAmenities;